import React, { useEffect, useCallback } from 'react';
import { useFormikContext } from 'formik';
import { PaymentFormValues } from 'types/payments';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import Box from '@mui/material/Box';
import * as IconMDI from 'mdi-material-ui';
import useStyles from './styles';
import PaymentFormCardFields from './PaymentFormCardFields';
import { CardSectionHeader, PaymentType, RadioCard } from '@elevator/ui';

/**
 * PaymentForm Component
 *
 * Reusable payment method selection and card form for checkout flows. Allows users to choose
 * between Stripe (credit card) and PayPal. When Stripe is selected, displays the Stripe
 * Payment Element only; Stripe collects card and billing details (name, address, etc.)
 * inside the element. Parent flows can read billing from the PaymentMethod returned by
 * createPaymentMethod() if needed.
 *
 * Uses Formik context typed with PaymentFormValues (cardInfo only required). Does not
 * render total, submit buttons, or account fields—compose those in the parent.
 *
 * Requires Formik and Stripe Elements context from a parent. Set Elements options.appearance
 * (e.g. theme: 'stripe' | 'night') to match your app theme.
 *
 * @param props - Component props
 * @param props.paymentType - The currently selected payment type
 * @param props.setPaymentType - Callback to update the payment type
 */
const PaymentForm = ({ paymentType, setPaymentType }: PaymentFormProps) => {
  const { classes } = useStyles();

  const formik = useFormikContext<PaymentFormValues & Record<string, unknown>>();
  const {
    values,
    errors,
    touched,
    validateForm,
    setFieldValue,
    handleChange,
    setFieldTouched,
  } = formik;

  const paymentValues: PaymentFormValues = {
    cardInfo: values.cardInfo,
  };

  const paymentErrors = {
    cardInfo: errors.cardInfo,
  };

  const paymentTouched = {
    cardInfo: touched.cardInfo,
  };

  useEffect(() => {
    validateForm();
  }, [paymentType, validateForm]);

  const handleRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPaymentType(event.target.value as PaymentType);
    },
    [setPaymentType],
  );

  const handleCardFormChange = useCallback(
    (
      fieldName: keyof PaymentFormValues,
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | null,
      imperativeValue?: unknown,
    ) => {
      if (imperativeValue !== undefined) {
        setFieldValue(fieldName as string, imperativeValue, true);
      } else if (event) {
        handleChange(event);
      }
      setFieldTouched(fieldName as string, true);
    },
    [setFieldValue, handleChange, setFieldTouched],
  );

  return (
    <Box flexDirection="column" width="100%" data-component="PaymentForm">
      <Typography variant="h6" className={classes.paymentInfoHeader}>
        Payment Info
      </Typography>
      <CardSectionHeader sectionTitle="Payment Method" />
      <RadioGroup
        id="paymentType"
        aria-label="Payment Type"
        name="paymentType"
        value={paymentType}
        onChange={handleRadioChange}
        className={classes.radioGroupContainer}
      >
        <RadioCard
          value="Stripe"
          Icon={IconMDI.CreditCardOutline}
          displayText="Card"
          active={paymentType === 'Stripe'}
        />
        <RadioCard
          value="PayPal"
          Icon={IconMDI.AccountCash}
          displayText="PayPal"
          active={paymentType === 'PayPal'}
        />
      </RadioGroup>
      {paymentType === 'PayPal' && (
        <Typography>PayPal will open in a new window upon checkout.</Typography>
      )}
      {paymentType === 'Stripe' && (
        <PaymentFormCardFields
          values={paymentValues}
          errors={paymentErrors}
          touched={paymentTouched}
          handleInputChange={handleCardFormChange}
        />
      )}
    </Box>
  );
};

export interface PaymentFormProps {
  /** The currently selected payment type */
  paymentType: PaymentType;
  /** Callback function to update the payment type */
  setPaymentType: (val: PaymentType) => void;
}

export default PaymentForm;
